/** KIT INSTAGRAM — les visuels que le pro poste en story.
 *  Les images sont rendues par le serveur (mêmes polices partout) et récupérées
 *  en blob : une balise <img> ne peut pas porter de jeton d'authentification. */
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, RefreshCw, Share2 } from "lucide-react";
import { api, messageErreur } from "../api";
import { Btn, EnTete, Micro, Squelette } from "../components/Bits";
import { urlPhoto } from "../components/Photos";
import { isoJour, jourLong } from "../lib/format";
import { THEMES } from "../lib/themes";
import { useApp } from "../App";

const PERIODES = [
  { c: "jour", l: "Le jour" },
  { c: "semaine", l: "La semaine" },
  { c: "mois", l: "Le mois" },
];

const AFFICHES = [
  [1, "Réserve en ligne", "24 h/24, sans passer par les messages"],
  [2, "Mes DM sont fermés", "Pour recentrer les rendez-vous sur ton lien"],
  [3, "Une place se libère ?", "Pour faire réagir sur tes annulations"],
  [4, "Complet cette semaine", "Pour montrer que ça tourne, et renvoyer sur la suite"],
  [5, "Nouvelle prestation", "Pour annoncer une nouveauté déjà réservable"],
  [6, "Je reprends les rendez-vous", "Après une pause, une fermeture, un congé"],
  [7, "Pense à ta retouche", "Le rappel doux qui fait revenir"],
  [8, "Offre de la semaine", "Quand tu veux remplir vite"],
  [9, "Merci pour ta confiance", "Pour entretenir le lien, sans rien vendre"],
  [10, "Je ferme quelques jours", "Pour prévenir, et faire réserver le retour"],
];

export default function Story() {
  const { compte } = useApp();
  const [type, setType] = useState("dispos");
  const [periode, setPeriode] = useState("semaine");
  const [depuis, setDepuis] = useState(isoJour());
  const [modele, setModele] = useState(1);
  const [sombre, setSombre] = useState(false);
  const [couleur, setCouleur] = useState("");
  const [titre, setTitre] = useState("");
  const [surTitre, setSurTitre] = useState("");
  const [texte, setTexte] = useState("");
  const [photoId, setPhotoId] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [url, setUrl] = useState(null);
  const [charge, setCharge] = useState(true);
  const [erreur, setErreur] = useState(null);

  const generer = useCallback(async () => {
    setCharge(true);
    setErreur(null);
    try {
      const chemin = type === "dispos" ? "/story/dispos" : "/story/reserve";
      const params =
        type === "dispos" ? { periode, depuis, sombre } : { modele, sombre };
      if (photoId) params.photo_id = photoId;
      if (couleur) params.couleur = couleur;
      if (titre.trim()) params.titre = titre.trim();
      if (type === "reserve") {
        if (surTitre.trim()) params.sur_titre = surTitre.trim();
        if (texte.trim()) params.texte = texte.trim();
      }
      const { data } = await api.get(chemin, { params, responseType: "blob" });
      setUrl((ancien) => {
        if (ancien) URL.revokeObjectURL(ancien);
        return URL.createObjectURL(data);
      });
    } catch (e) {
      setErreur(messageErreur(e, "Impossible de générer le visuel."));
    } finally {
      setCharge(false);
    }
  }, [type, periode, depuis, modele, sombre, photoId, couleur, titre, surTitre, texte]);

  useEffect(() => {
    api
      .get("/photos", { params: { portfolio: true } })
      .then(({ data }) => setPhotos(data))
      .catch(() => setPhotos([]));
  }, []);

  useEffect(() => {
    // le pro tape son texte : on attend qu'il souffle avant de redessiner
    const t = setTimeout(generer, 450);
    return () => clearTimeout(t);
  }, [generer]);

  const telecharger = () => {
    if (!url) return;
    const a = document.createElement("a");
    a.href = url;
    a.download =
      type === "dispos" ? `serva-dispos-${periode}-${depuis}.png` : `serva-affiche-${modele}.png`;
    a.click();
    toast.success("Visuel enregistré. Il est prêt à poster.");
  };

  const partager = async () => {
    try {
      const blob = await (await fetch(url)).blob();
      const fichier = new File([blob], "serva-story.png", { type: "image/png" });
      if (navigator.canShare?.({ files: [fichier] })) {
        await navigator.share({ files: [fichier], title: "Mes disponibilités" });
      } else {
        telecharger();
      }
    } catch {
      telecharger();
    }
  };

  return (
    <div className="pb-8" data-testid="page-story">
      <EnTete
        micro="Kit Instagram"
        titre="Ta story, prête à poster"
        sous="Le visuel est généré avec tes vraies disponibilités. Tu n'écris rien à la main."
      />

      {/* ------------------------------------------------------- que poster */}
      <div
        className="flex p-1 gap-1 mt-6 w-fit"
        style={{ background: "var(--surface-sunken)", borderRadius: "var(--radius-full)" }}
      >
        {[
          { c: "dispos", l: "Mes dispos" },
          { c: "reserve", l: "Réserve ici" },
        ].map((t) => {
          const on = type === t.c;
          return (
            <button
              key={t.c}
              onClick={() => setType(t.c)}
              data-testid={`story-type-${t.c}`}
              className="h-9 px-4 text-[13px] font-semibold cible-tactile"
              style={{
                borderRadius: "var(--radius-full)",
                background: on ? "var(--surface)" : "transparent",
                color: on ? "var(--text)" : "var(--text-subtle)",
                boxShadow: on ? "var(--shadow-sm)" : "none",
              }}
            >
              {t.l}
            </button>
          );
        })}
      </div>

      <div className="grid lg:grid-cols-[320px_1fr] gap-8 mt-7">
        {/* ------------------------------------------------------ réglages */}
        <div className="space-y-6">
          {type === "dispos" ? (
            <>
              <div>
                <Micro className="mb-3 block">Période</Micro>
                <div className="flex gap-2">
                  {PERIODES.map((p) => {
                    const on = periode === p.c;
                    return (
                      <button
                        key={p.c}
                        onClick={() => setPeriode(p.c)}
                        data-testid={`story-periode-${p.c}`}
                        className="h-10 px-3.5 text-[13px] font-semibold"
                        style={{
                          borderRadius: "var(--radius-md)",
                          background: on ? "var(--accent)" : "var(--surface)",
                          color: on ? "#fff" : "var(--text-muted)",
                          border: `1px solid ${on ? "var(--accent)" : "var(--border)"}`,
                        }}
                      >
                        {p.l}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <Micro className="mb-3 block">À partir du</Micro>
                <input
                  type="date"
                  value={depuis}
                  min={isoJour()}
                  onChange={(e) => setDepuis(e.target.value)}
                  data-testid="story-depuis"
                  className="w-full h-11 px-3.5 text-[15px] outline-none focus:border-[var(--accent)]"
                  style={{
                    background: "var(--surface)",
                    border: "1px solid var(--border)",
                    borderRadius: "var(--radius-md)",
                  }}
                />
                <p className="text-[12.5px] mt-2" style={{ color: "var(--text-subtle)" }}>
                  {jourLong(depuis)}
                </p>
              </div>
            </>
          ) : (
            <div>
              <Micro className="mb-3 block">Affiche</Micro>
              <div className="space-y-2">
                {AFFICHES.map(([n, t, d]) => {
                  const on = modele === n;
                  return (
                    <button
                      key={n}
                      onClick={() => setModele(n)}
                      data-testid={`story-modele-${n}`}
                      className="w-full text-left p-3.5"
                      style={{
                        background: "var(--surface)",
                        border: `1px solid ${on ? "var(--accent)" : "var(--border)"}`,
                        borderRadius: "var(--radius-md)",
                      }}
                    >
                      <span
                        className="block text-[14.5px] font-semibold"
                        style={{ color: on ? "var(--accent)" : "var(--text)" }}
                      >
                        {t}
                      </span>
                      <span className="block text-[13px]" style={{ color: "var(--text-muted)" }}>
                        {d}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div>
            <Micro className="mb-3 block">Couleur</Micro>
            <div className="flex gap-2 flex-wrap">
              {[
                ["", "Comme ma page", compte?.theme === "perso" ? compte?.marque?.accent : null],
                ...(compte?.marque?.accent ? [["marque", compte.marque.nom || "Ma marque", compte.marque.accent]] : []),
                ...Object.entries(THEMES).map(([c, t]) => [c, t.nom, t.accent]),
              ].map(([c, l, pastille]) => {
                const on = couleur === c;
                return (
                  <button
                    key={c || "defaut"}
                    onClick={() => setCouleur(c)}
                    data-testid={`story-couleur-${c || "page"}`}
                    className="inline-flex items-center gap-2 h-10 pl-2 pr-3.5 text-[13px] font-semibold"
                    style={{
                      borderRadius: "var(--radius-full)",
                      background: "var(--surface)",
                      border: `1px solid ${on ? "var(--accent)" : "var(--border)"}`,
                      color: on ? "var(--text)" : "var(--text-muted)",
                    }}
                  >
                    <span
                      style={{
                        height: 20,
                        width: 20,
                        borderRadius: 999,
                        background: pastille || "var(--accent)",
                        border: "1px solid rgba(20,20,19,.08)",
                      }}
                    />
                    {l}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <Micro className="mb-3 block">Mes mots</Micro>
            <input
              value={titre}
              onChange={(e) => setTitre(e.target.value.slice(0, 44))}
              placeholder={type === "dispos" ? "Titre (sinon : Mes places cette semaine)" : "Titre de l’affiche"}
              data-testid="story-titre"
              className="w-full h-11 px-3.5 text-[15px] outline-none focus:border-[var(--accent)]"
              style={{
                background: "var(--surface)",
                border: "1px solid var(--border)",
                borderRadius: "var(--radius-md)",
              }}
            />
            {type === "reserve" && (
              <>
                <input
                  value={surTitre}
                  onChange={(e) => setSurTitre(e.target.value.slice(0, 32))}
                  placeholder="Petite ligne du haut (ex : 24 h/24)"
                  data-testid="story-surtitre"
                  className="w-full h-11 px-3.5 mt-2 text-[15px] outline-none focus:border-[var(--accent)]"
                  style={{
                    background: "var(--surface)",
                    border: "1px solid var(--border)",
                    borderRadius: "var(--radius-md)",
                  }}
                />
                <textarea
                  value={texte}
                  onChange={(e) => setTexte(e.target.value.slice(0, 180))}
                  placeholder="Ton message (2 lignes maximum)"
                  rows={3}
                  data-testid="story-texte"
                  className="w-full p-3 mt-2 text-[15px] outline-none focus:border-[var(--accent)]"
                  style={{
                    background: "var(--surface)",
                    border: "1px solid var(--border)",
                    borderRadius: "var(--radius-md)",
                    resize: "vertical",
                  }}
                />
              </>
            )}
            <div className="flex items-center justify-between mt-2">
              <p className="text-[12.5px]" style={{ color: "var(--text-subtle)" }}>
                Laisse vide pour garder le texte du modèle.
              </p>
              {(titre || surTitre || texte) && (
                <button
                  onClick={() => {
                    setTitre("");
                    setSurTitre("");
                    setTexte("");
                  }}
                  data-testid="story-effacer-texte"
                  className="text-[12.5px] font-semibold"
                  style={{ color: "var(--accent)" }}
                >
                  Effacer
                </button>
              )}
            </div>
          </div>

          <div>
            <Micro className="mb-3 block">Fond</Micro>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => setPhotoId(null)}
                data-testid="story-fond-couleur"
                className="h-14 px-4 text-[13px] font-semibold"
                style={{
                  borderRadius: "var(--radius-md)",
                  background: "var(--accent-soft)",
                  color: "var(--accent)",
                  border: `1px solid ${!photoId ? "var(--accent)" : "var(--border)"}`,
                }}
              >
                Mes couleurs
              </button>
              {photos.map((p) => (
                <button
                  key={p.id}
                  onClick={() => setPhotoId(p.id)}
                  data-testid={`story-fond-${p.id}`}
                  className="h-14 w-14 overflow-hidden"
                  style={{
                    borderRadius: "var(--radius-md)",
                    border: `2px solid ${photoId === p.id ? "var(--accent)" : "var(--border)"}`,
                  }}
                >
                  <img src={urlPhoto(p.url)} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
            <p className="text-[12.5px] mt-2" style={{ color: "var(--text-subtle)" }}>
              {photos.length === 0
                ? "Ajoute des photos à ton portfolio pour les utiliser en fond."
                : "Une de tes photos en fond, voilée pour que le texte reste lisible."}
            </p>
          </div>

          <div>
            <Micro className="mb-3 block">Ambiance</Micro>
            <div className="flex gap-2">
              {[
                { v: false, l: "Clair" },
                { v: true, l: "Sombre" },
              ].map((a) => {
                const on = sombre === a.v;
                return (
                  <button
                    key={a.l}
                    onClick={() => setSombre(a.v)}
                    data-testid={`story-ambiance-${a.l.toLowerCase()}`}
                    className="h-10 px-4 text-[13px] font-semibold"
                    style={{
                      borderRadius: "var(--radius-md)",
                      background: on ? "var(--accent)" : "var(--surface)",
                      color: on ? "#fff" : "var(--text-muted)",
                      border: `1px solid ${on ? "var(--accent)" : "var(--border)"}`,
                    }}
                  >
                    {a.l}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Btn icone={Download} onClick={telecharger} data-testid="story-telecharger">
              Enregistrer
            </Btn>
            <Btn variante="fantome" icone={Share2} onClick={partager} data-testid="story-partager">
              Partager
            </Btn>
            <Btn variante="nu" icone={RefreshCw} onClick={generer} data-testid="story-regenerer">
              Régénérer
            </Btn>
          </div>

          <p className="text-[12.5px]" style={{ color: "var(--text-subtle)" }}>
            Format 1080 × 1920, prêt pour une story. Sur téléphone, « Partager » ouvre
            directement Instagram.
          </p>
        </div>

        {/* -------------------------------------------------------- aperçu */}
        <div className="flex justify-center">
          {charge ? (
            <div style={{ width: 300 }}>
              <Squelette lignes={1} />
            </div>
          ) : erreur ? (
            <p className="text-[14px]" style={{ color: "var(--danger)" }}>
              {erreur}
            </p>
          ) : (
            <img
              src={url}
              alt="Aperçu de la story"
              data-testid="story-apercu"
              style={{
                width: 320,
                maxWidth: "100%",
                borderRadius: 22,
                border: "1px solid var(--border)",
                boxShadow: "var(--shadow-md)",
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}
